import CommentIcon from "@mui/icons-material/Comment";
import DeleteIcon from "@mui/icons-material/Delete";
import GradingIcon from "@mui/icons-material/Grading";
import GroupsIcon from "@mui/icons-material/Groups";
import IosShareIcon from "@mui/icons-material/IosShare";
import SendIcon from "@mui/icons-material/Send";
import WarningIcon from '@mui/icons-material/Warning';
import Badge from "@mui/material/Badge";
import Portal from "@mui/material/Portal";
import { styled } from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";
import { skipToken } from "@reduxjs/toolkit/query";
import { ConfirmResponse, useConfirmDialog } from "_shared/_providers/dialogs/confirm/confirm-dialog";
import { useDialog } from "_shared/_providers/dialogs/dialog";
import { useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import Loader from "_shared/_views/loading/loader";
import Guid from "_shared/guids/guid";
import _ from "lodash";
import {
  getErrorLoadingTeamById,
  getIsLoadingTeamById,
  getTeamById,
  populateMarketplaceTeamById,
} from "marketplace/teams/_store/marketplace-teams-redux-slice";
import { enqueueSnackbar } from "notistack";
import React, { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate, useSearchParams } from "react-router";
import { useSession } from "users/__session/session-context";
import { AccountType } from "users/account-types/account-type";
import { MaskedContracts, MaskedTeam } from "work/_shared/masked-fields";
import {
  ActionsContainer,
  ButtonContainer,
  Content,
  DialogTab,
  Header,
  TabContent,
  TabsContainer,
} from "work/_views/work-dialog-styled-components";
import ProposalBuilder from "work/proposals/__builder/proposal-builder";
import {
  useDeleteProposalMutation,
  useGetProposalByIdQuery,
  useGiveReviewerApprovalMutation,
  useSubmitProposalMutation,
} from "work/proposals/_api/proposal-api";
import {
  replaceUpdatedProposalBuilder,
  getOriginalProposalBuilderByProposalId,
  getUpdatedProposalBuilderByProposalId,
  getProposalById,
} from "work/proposals/_store/proposals-redux-slice";
import {
  CommentButtonLabel,
  CommentButtons,
  Fieldset,
  HighlightCommentButton,
  ProposalActionButton,
  ProposalActions,
  ReviewerBadge,
} from "work/proposals/_views/_shared/proposal-dialog-styled-components";
import ConflictsTab from "work/proposals/_views/drafts/dialog-tabs/draft-proposal-conflicts-tab";
import DetailsTab from "work/proposals/_views/drafts/dialog-tabs/draft-proposal-details-tab";
import DiscountTab from "work/proposals/_views/drafts/dialog-tabs/draft-proposal-discount-tab";
import FeeScheduleTab from "work/proposals/_views/drafts/dialog-tabs/draft-proposal-fee-schedule-tab";
import PoliciesTab from "work/proposals/_views/drafts/dialog-tabs/draft-proposal-policies-tab";
import TeamTab from "work/proposals/_views/drafts/dialog-tabs/draft-proposal-team-tab";
import {
  getArePendingComments,
  updateAutoGeneratedComments,
} from "work/proposals/comments/_store/comments-redux-slice";
import CommentsDrawer from "work/proposals/comments/_view/comments-drawer";
import AutoCommentGenerator from "work/proposals/comments/auto-comments/auto-comment-generator";
import {
  getCommentThreadsByProposal,
  getIsLoadingCommentThreadsByProposal,
} from "work/proposals/comments/threads/_store/comment-thread-redux-slice";
import IssueTraverser from "work/proposals/issues/_views/proposal-issue-traverser";
import ProposalIssuesBadge from "work/proposals/issues/_views/proposal-issues-badge";
import ProposalIssues from "work/proposals/issues/proposal-issues";
import Proposal from "work/proposals/proposal";
import ProposalField, { ProposalFieldCategory, ProposalFieldName } from "work/proposals/proposal-fields/proposal-field";
import ReviewerSelector from "work/proposals/reviewers/_views/reviewer-selector";
import ProposalReviewer from "work/proposals/reviewers/proposal-reviewer";
import SelectedVendorTeamLeader from "work/work-agreements/teams/vendor-team/selection/selected-vendor-team-leader/selected-vendor-team-leader";
import SelectedVendorTeam from "work/work-agreements/teams/vendor-team/selection/selected-vendor-team/selected-vendor-team";
import WorkAgreement from "work/work-agreements/work-agreement";

const SecondaryActions = styled("section")(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
  },
  alignItems: "center",
  display: "flex",
  justifyContent: "space-between",
  marginBottom: theme.spacing(2),
  width: "100%",
}));

const ProposalActionSpan = styled("span")(({ theme }) => ({
  minWidth: theme.spacing(24),
  width: "100%",
}));

const NoBuilderLoader = styled(Loader)(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  justifyContent: "center",
  height: "100%",
  padding: theme.spacing(2),
  width: "100%",
}));

type ProposalDraftDialogProps = {
  proposalId?: Guid;
  disableEditing?: boolean;
  isSaving: boolean;
  onSaveAndShareChanges: (args?: { proposal?: Proposal; confirmShare?: boolean }) => Promise<string | null | undefined>;
};

export default function ProposalDraftDialog(props: Readonly<ProposalDraftDialogProps>) {
  const { proposalId, disableEditing, isSaving, onSaveAndShareChanges } = props;

  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const dispatch = useAttorneyHubDispatch();
  const confirm = useConfirmDialog();
  const { closeDialog } = useDialog();
  const session = useSession();

  const [activeTab, setActiveTab] = React.useState<ProposalFieldCategory>(ProposalFieldCategory.Details);
  const [activeReviewField, setActiveReviewField] = React.useState<ProposalField>();

  const [currentCommentField, setCurrentCommentField] = React.useState<ProposalField | undefined>();
  const [keepCommentsPanelClosed, setKeepCommentsPanelClosed] = React.useState<boolean>(false);
  const [commentsPanelIsOpen, setCommentsPanelIsOpen] = React.useState<boolean>(false);
  const [commentsPanelTitle, setCommentsPanelTitle] = React.useState<string | undefined>();

  const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false);
  const [isDeleting, setIsDeleting] = React.useState<boolean>(false);
  const [reviewerSelectorAnchor, setReviewerSelectorAnchor] = React.useState<HTMLButtonElement>();
  const [isManagingReviewers, setIsManagingReviewers] = React.useState<boolean>(false);

  const [isSelectingClient, setIsSelectingClient] = React.useState<boolean>(false);

  const preSelectedLeaderInfo = SelectedVendorTeamLeader.fromURLSearchParams(searchParams);
  const preSelectedTeamInfo = SelectedVendorTeam.fromURLSearchParams(searchParams);
  const preSelectedMarketplaceTeam = useSelector(getTeamById(preSelectedTeamInfo?.targetTeamId));
  const isLoadingPreSelectedMarketplaceTeam = useSelector(getIsLoadingTeamById(preSelectedTeamInfo?.targetTeamId));
  const wasErrorLoadingPreselectedMarketplaceTeam = useSelector(
    getErrorLoadingTeamById(preSelectedTeamInfo?.targetTeamId),
  );
  const existingWorkAgreement: WorkAgreement = location.state?.workAgreement;

  const proposalJson = useSelector(getProposalById(proposalId));
  const proposal = useMemo(() => {
    if (!proposalJson) return;
    return Proposal.fromJSON({
      json: proposalJson,
      currentUser: session.user!,
    });
  }, [proposalJson, session.user?.id?.value]);

  const hasDocs =
    MaskedContracts.isPresentNotMasked(proposalJson?.conflicts)
    && (proposalJson.conflicts.documents?.length ?? 0) > 0;

  const proposalQuery = useGetProposalByIdQuery(
    !proposalId || proposalId.isEmpty || hasDocs
      ? skipToken
      : { proposalId: proposalId?.value ?? Guid.EMPTY.value, session },
  );
  const { clientReviewersKey, vendorReviewersKey } = useMemo(() => {
    const clientReviewersKey = proposal?.clientReviewers.map((reviewer) => reviewer.userId.value).join(",");
    const vendorReviewersKey = proposal?.vendorReviewers.map((reviewer) => reviewer.userId.value).join(",");
    return { clientReviewersKey, vendorReviewersKey };
  }, [proposal, session.user?.id?.value]);

  const originalBuilderJSON = useSelector(getOriginalProposalBuilderByProposalId(proposalId ?? Guid.EMPTY));
  const originalBuilder = useMemo(() => {
    return ProposalBuilder.fromJSON({
      json: originalBuilderJSON,
      session,
    });
  }, [originalBuilderJSON]);

  const updatedBuilderJSON = useSelector(getUpdatedProposalBuilderByProposalId(proposalId ?? Guid.EMPTY));
  const updatedBuilder = useMemo(() => {
    return ProposalBuilder.fromJSON({
      json: updatedBuilderJSON,
      session,
    });
  }, [updatedBuilderJSON]);

  const commentThreads = useSelector(getCommentThreadsByProposal(proposal));
  const arePendingComments = useSelector(getArePendingComments(proposal));
  const isLoadingCommentThreads = useSelector(getIsLoadingCommentThreadsByProposal(proposal));
  const isLoading = proposalQuery.isLoading || isLoadingCommentThreads;

  const [submitProposalQuery] = useSubmitProposalMutation();
  const [deleteProposalQuery] = useDeleteProposalMutation();
  const [giveReviewerApprovalQuery, giveReviewerApprovalQueryResult] = useGiveReviewerApprovalMutation();

  const isReviewing = useMemo(() => {
    return (
      [...(proposal?.clientReviewers ?? []), ...(proposal?.vendorReviewers ?? [])].some((reviewer) =>
        reviewer.userId.isEqualTo(session.user?.id),
      ) ?? false
    );
  }, [proposal, clientReviewersKey, vendorReviewersKey, session.user]);
  const vendorReviewerCount = updatedBuilder?.currentSpec?.vendorReviewers?.length ?? 0;
  const clientReviewerCount = updatedBuilder?.currentSpec?.clientReviewers?.length ?? 0;
  const hasReviewers = session.context?.viewingAsVendor ? vendorReviewerCount > 0 : clientReviewerCount > 0;

  const generalFieldUnreadCount = commentThreads
    ?.filter((thread) => thread.field.isEqualTo(ProposalField.General))
    .reduce((acc, thread) => acc + (thread.numUnreadMessages ?? 0), 0) ?? 0;

  const issues = useMemo(() => ProposalIssues.fromBuilder(updatedBuilder ?? undefined), [updatedBuilder]);

  useEffect(() => {
    if (
      !preSelectedTeamInfo?.targetTeamId ||
      isLoadingPreSelectedMarketplaceTeam ||
      preSelectedMarketplaceTeam ||
      wasErrorLoadingPreselectedMarketplaceTeam
    )
      return;
    dispatch(populateMarketplaceTeamById({ session, teamId: preSelectedTeamInfo.targetTeamId }));
  }, [
    preSelectedTeamInfo?.targetTeamId,
    isLoadingPreSelectedMarketplaceTeam,
    wasErrorLoadingPreselectedMarketplaceTeam,
  ]);

  useEffect(() => {
    if (proposalId && !proposal) return; // still loading proposal
    if (updatedBuilderJSON || updatedBuilder) {
      return;
    }

    const initializedBuilder =
      originalBuilder ??
      new ProposalBuilder({
        session,
        startingSpec: proposal?.spec,
        existingWorkAgreement,
        preSelectedClientLeader: preSelectedLeaderInfo?.isClientRep
          ? preSelectedLeaderInfo?.toEntityMember()
          : undefined,
        preSelectedVendorLeader: preSelectedLeaderInfo?.isVendorRep
          ? preSelectedLeaderInfo?.toEntityMember()
          : undefined,
        preSelectedTeam: preSelectedMarketplaceTeam?.toWorkTeam(),
      });
    dispatch(replaceUpdatedProposalBuilder({ proposalId: proposalId ?? Guid.EMPTY, builder: initializedBuilder }));
  }, [
    proposalId,
    proposalQuery.data,
    proposal?.spec,
    originalBuilder,
    preSelectedTeamInfo?.targetTeamId,
    preSelectedLeaderInfo,
  ]);

  useEffect(() => {
    if (!updatedBuilder || !proposalId) return;
    void generateChangeAutoComments(updatedBuilder);
  }, [updatedBuilder?.lastUpdated.toISOString(), proposalId?.value]);

  useEffect(() => {
    if (!session?.canSwitchContext || !proposal) return;
    if (
      MaskedTeam.isPresentNotMasked(proposal.vendorTeam) &&
      MaskedTeam.isPresentNotMasked(proposal.clientLeader) &&
      session.context?.viewingAsVendor &&
      proposal.clientLeader?.userId.isEqualTo(session.user?.id)
    ) {
      void session.setAccountViewType(AccountType.Client);
    } else if (
      MaskedTeam.isPresentNotMasked(proposal.vendorTeam) &&
      (proposal.vendorTeam?.leader?.userId.isEqualTo(session.user?.id) ||
        proposal.vendorTeam?.members?.some((member) => member.userId.isEqualTo(session.user?.id)))
    ) {
      void session.setAccountViewType(AccountType.Vendor);
    }
  }, [proposal?.id?.value]);

  useEffect(() => {
    if (activeReviewField?.category !== activeTab) {
      setActiveTab(activeReviewField?.category ?? activeTab);
    }
    if (activeReviewField && proposal?.createdDate && !keepCommentsPanelClosed) {
      handleOpenCommentsPanel(activeReviewField);
    }
  }, [activeReviewField]);

  useEffect(() => {
    // if (activeReviewField?.category === activeTab) return;
    const proposalFieldCategories = Object.values(ProposalFieldCategory);
    const proposalFieldNames = Object.values(ProposalFieldName);
    const tabIssues: ProposalField[] = issueFields.filter((field) => field.category === activeTab) ?? [];
    const tabUnreadComments: ProposalField[] = _.uniqBy(
      commentThreads
        ?.filter((thread) => thread.numUnreadMessages && thread.field.category === activeTab)
        .map((thread) => thread.field) ?? [],
      (field) => field.toJSON(),
    );
    const fieldsPendingReview: ProposalField[] = _.chain([...tabUnreadComments, ...tabIssues])
      .uniqBy((field) => `${field.category}-${field.name}-${field.id?.value ?? ""}`)
      .sortBy([
        (field) => proposalFieldCategories.indexOf(field.category),
        (field) => proposalFieldNames.indexOf(field.name),
      ])
      .value();
    if (fieldsPendingReview[0] && fieldsPendingReview[0].category !== activeTab) {
      setActiveReviewField(undefined);
      return;
    }
    setActiveReviewField(fieldsPendingReview[0]);
  }, [activeTab, issues.key]);

  function handleTabChange(_event: React.ChangeEvent<object> | null, newTab: ProposalFieldCategory) {
    setActiveTab(newTab);
  }

  function handleManageReviewersClicked(event: React.MouseEvent<HTMLButtonElement>) {
    setReviewerSelectorAnchor(event.currentTarget);
    setIsManagingReviewers(true);
  }

  async function handleSubmitClicked() {
    if (!updatedBuilder) {
      throw new Error("Proposal cannot be submitted without a builder.");
    }

    try {
      let message: string;
      const clientReviewers = updatedBuilder?.currentSpec.clientReviewers ?? proposal?.clientReviewers ?? [];
      const vendorReviewers = updatedBuilder?.currentSpec.vendorReviewers ?? proposal?.vendorReviewers ?? [];
      if (
        (session.context?.viewingAsVendor &&
          vendorReviewers.length &&
          vendorReviewers.length !== vendorReviewers.filter((reviewer) => reviewer.dateApproved).length) ||
        (!session.context?.viewingAsVendor &&
          clientReviewers.length &&
          clientReviewers.length !== clientReviewers.filter((reviewer) => reviewer.dateApproved).length)
      ) {
        message = "Not all reviewers have reviewed the proposal. Do you still want to submit the proposal?";
      } else {
        message = "Submit the proposal now?";
      }

      const response = await confirm({
        title: "Submit Proposal",
        message: message,
        okButtonText: "Submit",
      });
      if (response === ConfirmResponse.Cancel) return;

      setIsSubmitting(true);
      const draftProposalIdValue = await onSaveAndShareChanges({ confirmShare: false });

      if (!draftProposalIdValue) {
        throw new Error("Failed to save proposal before submission.");
      }
      await submitProposalQuery({ proposalId: draftProposalIdValue }).unwrap();
      enqueueSnackbar("Proposal submitted successfully", { variant: "success" });
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Failed to submit proposal", { variant: "error" });
    } finally {
      setIsSubmitting(false);
    }
  }

  function shouldDisableTabs(): boolean {
    return isSaving || isManagingReviewers || isSelectingClient;
  }

  async function handleDeleteClicked() {
    if (proposal?.isArchived) return;
    if (!proposal?.id) {
      console.warn("Proposal not found");
      return;
    }

    try {
      const rejectResponse = await confirm({
        title: "Delete Draft Proposal?",
        message: "This cannot be undone.",
      });

      if (rejectResponse === ConfirmResponse.Cancel) return;

      setIsDeleting(true);
      navigate(`/proposals/drafts/`);
      await deleteProposalQuery({ proposalId: proposal.id.value }).unwrap();
      closeDialog();
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Failed to delete proposal", { variant: "error" });
    } finally {
      setIsDeleting(false);
    }
  }

  async function handleProposalBuilderUpdated(updatedProposalBuilder: ProposalBuilder) {
    if (proposal?.isArchived) return;
    dispatch(
      replaceUpdatedProposalBuilder({
        proposalId: proposal?.id ?? Guid.EMPTY,
        builder: updatedProposalBuilder,
      }),
    );
  }

  async function generateChangeAutoComments(proposalBuilder: ProposalBuilder) {
    if (!proposal?.id || proposal?.isArchived) return;
    const autoComments = new AutoCommentGenerator(
      session,
      proposal,
      proposalBuilder,
      commentThreads ?? [],
    ).generateAutoCommentsFromBuilder();
    dispatch(updateAutoGeneratedComments(autoComments));
  }

  const handleCloseCommentsPanel = () => {
    setKeepCommentsPanelClosed(true);
    setCommentsPanelIsOpen(false);
    adjustDialogPosition(true);
  };

  const handleOpenCommentsPanel = (field?: ProposalField, title?: string) => {
    if (field) setCurrentCommentField(field);
    if (title) setCommentsPanelTitle(title);
    setCommentsPanelIsOpen(true);
    adjustDialogPosition(false);
  };
  const adjustDialogPosition = (panelOpen: boolean) => {
    for (const dialog of document.getElementsByClassName("MuiDialog-root")) {
      dialog.setAttribute("style", `padding-right: ${panelOpen ? "0px" : "400px"}; transition: padding-right 225ms;`);
    }
  };

  async function handleMarkReviewedClicked() {
    if (!proposal?.id) throw new Error("No proposal to mark reviewed");

    try {
      const response = await confirm({
        title: "Mark As Reviewed",
        message: "Are you sure you want to mark this proposal as reviewed?",
        okButtonText: "Mark Reviewed",
      });

      if (response === ConfirmResponse.Cancel) return;

      await giveReviewerApprovalQuery({ proposalId: proposal.id.value }).unwrap();
      enqueueSnackbar("Proposal marked as reviewed", { variant: "success" });
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Failed to mark proposal as reviewed", { variant: "error" });
    }
  }

  const hasUserApprovedReview = useMemo(() => {
    const reviewers: ProposalReviewer[] = [...(proposal?.clientReviewers ?? []), ...(proposal?.vendorReviewers ?? [])];

    return reviewers.some((reviewer) => reviewer.userId.isEqualTo(session.user?.id) && reviewer.dateApproved);
  }, [proposal?.reviewersKey]);

  function getShareButtonLabel() {
    if (isSubmitting) return "Submitting...";
    if (isSaving) return "Saving...";
    if (!hasReviewers) return "No Reviewers";
    if (arePendingComments && !proposal?.isModifiedByBuilder(updatedBuilder)) {
      return "Share Comments";
    }
    if (proposal?.isModifiedByBuilder(updatedBuilder)) {
      return "Share Changes";
    }
    return "Nothing To Share";
  }

  function handleCommentsClicked(field: ProposalField, name?: string) {
    if (proposal?.id && !keepCommentsPanelClosed) {
      handleOpenCommentsPanel(field, name);
    } else {
      setCurrentCommentField(field);
      setCommentsPanelTitle(name);
    }
  }

  const commentsToolTip = proposal?.id ? "Add comments" : "Save proposal to add comments";
  const manageReviewersDisabled =
    !updatedBuilder ||
    isSaving ||
    isSelectingClient ||
    isSelectingClient ||
    isManagingReviewers ||
    reviewerSelectorAnchor !== undefined ||
    !proposal?.id ||
    disableEditing ||
    isReviewing;
  const reviewers =
    (session.context?.viewingAsVendor
      ? updatedBuilder?.currentSpec.vendorReviewers
      : updatedBuilder?.currentSpec.clientReviewers) ?? [];
  const totalApprovals = useMemo(() => reviewers.filter((reviewer) => reviewer.dateApproved).length, [reviewers]);
  const totalReviewers = reviewers.length ?? 0;
  const areChangesToShare = proposal?.isModifiedByBuilder(updatedBuilder) || arePendingComments;
  const issueFields = useMemo(() => {
    return _.uniqBy(issues?.entries.map((issue) => issue.field) ?? [], (field) => field.toJSON());
  }, [issues]);
  const unreadCommentFields = useMemo(() => {
    return _.uniqBy(
      commentThreads?.filter((thread) => thread.numUnreadMessages).map((thread) => thread.field) ?? [],
      (field) => field.toJSON(),
    );
  }, [commentThreads]);

  return (
    <>
      {!updatedBuilder && <NoBuilderLoader />}
      {updatedBuilder && (
        <>
          <Header>
            <TabsContainer
              variant="scrollable"
              scrollButtons="auto"
              indicatorColor="primary"
              textColor="primary"
              value={activeTab}
              onChange={handleTabChange}
            >
              <DialogTab
                value={ProposalFieldCategory.Details}
                disabled={shouldDisableTabs()}
                label={
                  <ProposalIssuesBadge
                    issues={issues}
                    commentThreads={commentThreads ?? []}
                    category={ProposalFieldCategory.Details}
                  />
                }
              />
              <DialogTab
                value={ProposalFieldCategory.Team}
                disabled={shouldDisableTabs()}
                label={
                  <ProposalIssuesBadge
                    issues={issues}
                    commentThreads={commentThreads ?? []}
                    category={ProposalFieldCategory.Team}
                  />
                }
              />
              <DialogTab
                value={ProposalFieldCategory.FeeSchedule}
                disabled={shouldDisableTabs()}
                label={
                  <ProposalIssuesBadge
                    issues={issues}
                    commentThreads={commentThreads ?? []}
                    category={ProposalFieldCategory.FeeSchedule}
                  />
                }
              />
              <DialogTab
                value={ProposalFieldCategory.Conflicts}
                disabled={shouldDisableTabs()}
                label={
                  <ProposalIssuesBadge
                    issues={issues}
                    commentThreads={commentThreads ?? []}
                    category={ProposalFieldCategory.Conflicts}
                  />
                }
              />
              <DialogTab
                value={ProposalFieldCategory.Policies}
                disabled={shouldDisableTabs()}
                label={
                  <ProposalIssuesBadge
                    issues={issues}
                    commentThreads={commentThreads ?? []}
                    category={ProposalFieldCategory.Policies}
                  />
                }
              />
              <DialogTab
                value={ProposalFieldCategory.Discount}
                disabled={shouldDisableTabs()}
                label={
                  <ProposalIssuesBadge
                    issues={issues}
                    commentThreads={commentThreads ?? []}
                    category={ProposalFieldCategory.Discount}
                  />
                }
              />
            </TabsContainer>
          </Header>
          <Content>
            <TabContent>
              {updatedBuilder && (
                <>
                  {activeTab === ProposalFieldCategory.Details && (
                    <DetailsTab
                      commentThreads={commentThreads ?? []}
                      isSaved={Boolean(proposal?.id)}
                      issues={issues}
                      activeReviewField={activeReviewField}
                      proposalBuilder={updatedBuilder}
                      disableEditing={Boolean(disableEditing || (proposal && !proposal?.userCanEdit(session.user)))}
                      onSelectingClientChange={(isSelecting: boolean) => setIsSelectingClient(isSelecting)}
                      onProposalBuilderUpdated={handleProposalBuilderUpdated}
                      onCommentsClicked={handleCommentsClicked}
                    />
                  )}
                  {activeTab === ProposalFieldCategory.Team && (
                    <TeamTab
                      proposalBuilder={updatedBuilder}
                      isSaved={Boolean(proposal?.id)}
                      issues={issues}
                      disableEditing={Boolean(disableEditing || (proposal && !proposal?.userCanEdit(session.user)))}
                      activeReviewField={activeReviewField}
                      commentThreads={commentThreads ?? []}
                      commentsToolTip={commentsToolTip}
                      onProposalBuilderUpdated={handleProposalBuilderUpdated}
                      onCommentsClicked={handleCommentsClicked}
                    />
                  )}
                  {activeTab === ProposalFieldCategory.FeeSchedule && (
                    <FeeScheduleTab
                      proposalBuilder={updatedBuilder}
                      isSaved={Boolean(proposal?.id)}
                      issues={issues}
                      commentThreads={commentThreads}
                      commentsToolTip={commentsToolTip}
                      disableEditing={Boolean(disableEditing || (proposal && !proposal?.userCanEdit(session.user)))}
                      activeReviewField={activeReviewField}
                      onProposalBuilderUpdated={handleProposalBuilderUpdated}
                      onCommentsClicked={handleCommentsClicked}
                    />
                  )}
                  {activeTab === ProposalFieldCategory.Conflicts && (
                    <ConflictsTab
                      proposalBuilder={updatedBuilder}
                      isSaved={Boolean(proposal?.id)}
                      issues={issues}
                      commentThreads={commentThreads}
                      commentsToolTip={commentsToolTip}
                      disableEditing={Boolean(disableEditing || (proposal && !proposal?.userCanEdit(session.user)))}
                      activeReviewField={activeReviewField}
                      onProposalBuilderUpdated={handleProposalBuilderUpdated}
                      onCommentsClicked={handleCommentsClicked}
                    />
                  )}
                  {activeTab === ProposalFieldCategory.Policies && (
                    <PoliciesTab
                      proposalBuilder={updatedBuilder}
                      isSaved={Boolean(proposal?.id)}
                      issues={issues}
                      commentThreads={commentThreads}
                      disableEditing={Boolean(disableEditing || (proposal && !proposal?.userCanEdit(session.user)))}
                      activeReviewField={activeReviewField}
                      commentsToolTip={commentsToolTip}
                      onProposalBuilderUpdated={handleProposalBuilderUpdated}
                      onCommentsClicked={handleCommentsClicked}
                    />
                  )}
                  {activeTab === ProposalFieldCategory.Discount && (
                    <DiscountTab
                      proposalBuilder={updatedBuilder}
                      isSaved={Boolean(proposal?.id)}
                      issues={issues}
                      disableEditing={Boolean(disableEditing || (proposal && !proposal?.userCanEdit(session.user)))}
                      activeReviewField={activeReviewField}
                      commentThreads={commentThreads ?? []}
                      commentsToolTip={commentsToolTip}
                      onProposalBuilderUpdated={handleProposalBuilderUpdated}
                      onCommentsClicked={handleCommentsClicked}
                    />
                  )}
                </>
              )}
            </TabContent>
          </Content>
          <ActionsContainer>
            <SecondaryActions>
              <IssueTraverser
                issueFields={issueFields}
                unreadCommentFields={unreadCommentFields}
                currentField={activeReviewField}
                prevLabel="Prev Issue"
                nextLabel="Next Issue"
                onTraverseToNewField={setActiveReviewField}
              />
            </SecondaryActions>
            <ProposalActions>
              {!disableEditing && (
                <>
                  <Fieldset>
                    {!isReviewing && (
                      <>
                        <Tooltip
                          enterDelay={600}
                          placement="top"
                          title={proposal?.id ? "" : "Save proposal to add reviewers."}
                        >
                          <ProposalActionSpan>
                            <ProposalActionButton
                              variant="contained"
                              color="primary"
                              startIcon={<GroupsIcon />}
                              endIcon={
                                <ReviewerBadge
                                  invisible={manageReviewersDisabled || !reviewers.length}
                                  badgeContent={
                                    <Tooltip
                                      title={
                                        totalApprovals === totalReviewers
                                          ? "All reviewers approved"
                                          : "Awaiting reviewer responses"
                                      }
                                    >
                                      <span>
                                        <sup>{totalApprovals}</sup>&frasl;<sub>{totalReviewers}</sub>
                                      </span>
                                    </Tooltip>
                                  }
                                  color={totalReviewers === totalApprovals ? "success" : "warning"}
                                />
                              }
                              loading={isLoading}
                              disabled={isLoading || manageReviewersDisabled}
                              onClick={handleManageReviewersClicked}
                            >
                              Reviewers
                            </ProposalActionButton>
                          </ProposalActionSpan>
                        </Tooltip>
                        <ReviewerSelector
                          proposal={proposal ?? undefined}
                          proposalBuilder={updatedBuilder}
                          popoverAnchor={reviewerSelectorAnchor}
                          onPopoverClose={() => {
                            setReviewerSelectorAnchor(undefined);
                            setIsManagingReviewers(false);
                          }}
                          onReviewersChanged={(updatedReviewers) => {
                            let updatedProposalBuilder;
                            if (session.context?.viewingAsVendor) {
                              updatedProposalBuilder = updatedBuilder?.setVendorReviewers(updatedReviewers);
                            } else {
                              updatedProposalBuilder = updatedBuilder?.setClientReviewers(updatedReviewers);
                            }
                            void handleProposalBuilderUpdated(updatedProposalBuilder);
                          }}
                          onSend={() => proposal && onSaveAndShareChanges({ confirmShare: true })}
                        />
                      </>
                    )}
                    {isReviewing && (
                      <Tooltip
                        enterDelay={600}
                        placement="top"
                        title={hasUserApprovedReview ? "Proposal marked as reviewed" : "Mark proposal as reviewed"}
                      >
                        <span>
                          <ProposalActionButton
                            variant="contained"
                            color="primary"
                            startIcon={<GradingIcon />}
                            loading={isSaving}
                            disabled={
                              isLoading ||
                              !updatedBuilder ||
                              !commentThreads ||
                              isSaving ||
                              giveReviewerApprovalQueryResult.isLoading ||
                              isSelectingClient ||
                              isManagingReviewers ||
                              hasUserApprovedReview
                            }
                            onClick={handleMarkReviewedClicked}
                          >
                            {hasUserApprovedReview ? "Reviewed" : "Mark Reviewed"}
                          </ProposalActionButton>
                        </span>
                      </Tooltip>
                    )}
                    <Tooltip
                      enterDelay={600}
                      placement="top"
                      title={
                        areChangesToShare
                          ? "Share changes and/or comments for review"
                          : "No reviewers or no unshared changes/comments"
                      }
                    >
                      <span>
                        <ProposalActionButton
                          variant="contained"
                          color="primary"
                          startIcon={<IosShareIcon />}
                          loading={isSaving}
                          disabled={
                            isLoading ||
                            !areChangesToShare ||
                            isSaving ||
                            giveReviewerApprovalQueryResult.isLoading ||
                            isSelectingClient ||
                            isManagingReviewers ||
                            !hasReviewers
                          }
                          onClick={() => proposal && onSaveAndShareChanges({ confirmShare: true })}
                        >
                          {getShareButtonLabel()}
                        </ProposalActionButton>
                      </span>
                    </Tooltip>
                  </Fieldset>
                  {!disableEditing && !isReviewing && (
                    <Tooltip
                      enterDelay={600}
                      placement="top"
                      title={(() => {
                        if (proposal?.id && issues?.canSubmit) {
                          return "Submit proposal to other party";
                        } else if (!proposalId && issues?.canSubmit) {
                          return "Save proposal to submit"
                        }
                        return "Fill out required fields to submit";
                      })()}
                    >
                      <ProposalActionSpan>
                        <ProposalActionButton
                          variant="contained"
                          color="success"
                          startIcon={
                            (!proposal?.id && issues?.canSubmit)
                            ? <WarningIcon />
                            : <SendIcon />
                          }
                          loading={isSubmitting}
                          disabled={
                            Boolean(!proposal?.id) ||
                            isLoading ||
                            !updatedBuilder ||
                            isSaving ||
                            isSelectingClient ||
                            isManagingReviewers ||
                            !issues?.canSubmit ||
                            disableEditing ||
                            isReviewing
                          }
                          onClick={handleSubmitClicked}
                        >
                          Submit
                        </ProposalActionButton>
                      </ProposalActionSpan>
                    </Tooltip>
                  )}
                  {proposal?.creator?.userId.isEqualTo(session.user?.id) && (
                    <ProposalActionButton
                      variant="contained"
                      color="error"
                      startIcon={<DeleteIcon />}
                      loading={isDeleting}
                      disabled={
                        isLoading ||
                        !updatedBuilder ||
                        isSelectingClient ||
                        isDeleting ||
                        isSubmitting ||
                        isSaving ||
                        isManagingReviewers ||
                        !proposal?.id ||
                        isReviewing
                      }
                      onClick={handleDeleteClicked}
                    >
                      Delete Proposal
                    </ProposalActionButton>
                  )}
                </>
              )}
            </ProposalActions>
            <CommentButtons>
              <Tooltip
                enterDelay={600}
                placement="top-end"
                title={proposal?.id ? "" : "Save proposal to enable commenting"}
              >
                <ButtonContainer>
                  <HighlightCommentButton
                    active={activeReviewField?.isEqualTo(ProposalField.General)}
                    size="medium"
                    color="primary"
                    disabled={!proposal?.id || !updatedBuilder}
                    onClick={() => handleOpenCommentsPanel(ProposalField.General)}
                  >
                    <Badge
                      variant={generalFieldUnreadCount ? "standard" : "dot"}
                      color="secondary"
                      overlap="circular"
                      badgeContent={generalFieldUnreadCount > 0 ? generalFieldUnreadCount : undefined}
                      invisible={generalFieldUnreadCount === 0}
                    >
                      <CommentIcon fontSize="medium" />
                    </Badge>
                  </HighlightCommentButton>
                  <CommentButtonLabel
                    variant="button"
                    color={!proposal?.id || !updatedBuilder ? "darkgray" : "primary"}
                  >
                    Comments
                  </CommentButtonLabel>
                </ButtonContainer>
              </Tooltip>
            </CommentButtons>
            {proposal?.createdDate && (
              <Portal>
                <CommentsDrawer
                  proposal={proposal}
                  commentThreads={commentThreads}
                  currentCommentField={currentCommentField}
                  activeReviewField={activeReviewField}
                  isOpen={commentsPanelIsOpen}
                  isSaving={isSaving}
                  title={commentsPanelTitle}
                  onOpen={() => {
                    handleOpenCommentsPanel();
                    setKeepCommentsPanelClosed(false);
                  }}
                  onClose={handleCloseCommentsPanel}
                />
              </Portal>
            )}
          </ActionsContainer>
        </>
      )}
    </>
  );
}
